import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, BookOpen, Users, Lightbulb, Target, Award, Brain } from "lucide-react";

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-8 md:py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-secondary leading-tight mb-4">
                  Personalized learning activities for your child, in minutes.
                </h1>
              </div>
              <p className="text-base sm:text-lg text-foreground/80">
                AI-built, hands-on activities matched to your child's ability, not their age, and made from materials you already have at home. Every completed activity builds your child's exposure report, so on the days you wonder if you're doing enough, you can see that you are. Free to start.
              </p>
              <p className="text-sm font-medium text-primary" data-testid="hero-against-line">
                No labels. No ranks. No one-size-fits-all pacing.
              </p>
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4">
                  <Button
                    data-testid="generate-activity-btn"
                    size="lg"
                    className="rounded-full px-8 py-6 text-lg font-bold shadow-pop hover:shadow-pop-hover transform hover:-translate-y-1 transition-all duration-300 bg-primary hover:bg-primary/90"
                    onClick={() => navigate("/generate")}
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate Your First Activity
                  </Button>
                  <Button
                    data-testid="view-library-btn"
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-bold border-2 border-secondary text-secondary hover:bg-secondary hover:text-white transition-all duration-300"
                    onClick={() => navigate("/library")}
                  >
                    <BookOpen className="mr-2 h-5 w-5" />
                    View Activity Library
                  </Button>
                </div>
                <p className="text-sm text-secondary/70 font-medium" data-testid="hero-free-caption">
                  Free to start - no card needed
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1595987146581-b118732eb87a?crop=entropy&cs=srgb&fm=webp&w=800&q=80"
                srcSet="https://images.unsplash.com/photo-1595987146581-b118732eb87a?crop=entropy&cs=srgb&fm=webp&w=400&q=80 400w, https://images.unsplash.com/photo-1595987146581-b118732eb87a?crop=entropy&cs=srgb&fm=webp&w=800&q=80 800w, https://images.unsplash.com/photo-1595987146581-b118732eb87a?crop=entropy&cs=srgb&fm=webp&w=1200&q=80 1200w"
                sizes="(max-width: 768px) 100vw, 50vw"
                alt="Indian child learning at home with parent support"
                width="800"
                height="600"
                className="rounded-3xl shadow-pop w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Why - Addressing Critical Education Gaps */}
      <div id="why-revivedu" className="bg-muted py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-secondary mb-8">
            Why Revivedu?
          </h2>
          <div className="max-w-4xl mx-auto space-y-6 text-foreground/80 text-base sm:text-lg">
            <p>
              The Indian education system currently faces a significant gap in addressing the specialized needs of <strong>homeschooled children</strong> and <strong>gifted learners</strong> recognized under NEP 2020. Both groups require educational approaches that move beyond standardized content to embrace individualized learning.
            </p>
            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-card rounded-3xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-secondary mb-3 flex items-center">
                  <Award className="mr-2 h-6 w-6 text-primary" />
                  Gifted Learners
                </h3>
                <p className="text-foreground/80">
                  Despite NEP 2020 recognition, gifted learners lack systematic identification and personalized pathways challenging their unique abilities.
                </p>
              </div>
              <div className="bg-card rounded-3xl p-6 shadow-sm">
                <h3 className="text-xl font-bold text-secondary mb-3 flex items-center">
                  <BookOpen className="mr-2 h-6 w-6 text-primary" />
                  Homeschooled Children
                </h3>
                <p className="text-foreground/80">
                  Struggle to access structured yet flexible curriculum resources aligned with national standards (NIOS).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What - Our Solution */}
      <div className="bg-card py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-secondary mb-8">
            What is Revivedu?
          </h2>
          <div className="max-w-4xl mx-auto space-y-6 text-foreground/80 text-lg mb-12">
            <p>
              Revivedu integrates <strong>Howard Gardner's Multiple Intelligences Theory</strong> to provide a holistic approach to education, allowing parents and teachers to understand children's unique intelligence profiles and create tailored learning pathways.
            </p>
            <p>
              The platform offers structured access to the <strong>NIOS curriculum</strong> for grades 10 and 12 while supplementing it with AI-generated activities targeting specific intelligences.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-background rounded-3xl p-8 shadow-sm hover:shadow-pop transition-all duration-300">
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Brain className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-secondary mb-4">Multiple Intelligences Theory</h3>
              <p className="text-foreground/80">
                Activities targeting all 8 intelligence types, moving beyond traditional methods that favor only 2-3 types.
              </p>
            </div>
            <div className="bg-background rounded-3xl p-8 shadow-sm hover:shadow-pop transition-all duration-300">
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-secondary mb-4">AI-Powered Learning</h3>
              <p className="text-foreground/80">
                Advanced AI generates personalized activities based on intelligence types, age, and NIOS curriculum alignment.
              </p>
            </div>
            <div className="bg-background rounded-3xl p-8 shadow-sm hover:shadow-pop transition-all duration-300">
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-secondary mb-4">NEP 2020 Aligned</h3>
              <p className="text-foreground/80">
                All activities align with NCF-SE 2023 and NIOS standards, ensuring pedagogical soundness.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How - Platform Features */}
      <div id="how-it-works" className="bg-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-secondary mb-12">
            How It Works
          </h2>
          <div className="max-w-5xl mx-auto space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-secondary mb-2">Create Child Profile</h3>
                <p className="text-foreground/80">
                  Sign up and create a profile for your child, including their age, interests, and learning preferences.
                </p>
              </div>
            </div>
            
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-secondary mb-2">Generate Personalized Activities</h3>
                <p className="text-foreground/80">
                  Select subjects, intelligences, and available materials. Our AI creates comprehensive, curriculum-aligned activities instantly.
                </p>
              </div>
            </div>
            
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-secondary mb-2">Engage & Track Progress</h3>
                <p className="text-foreground/80">
                  Complete activities together, submit feedback and artifacts. Your child's exposure report grows with every activity - a record of what they have explored, never a score.
                </p>
              </div>
            </div>
            
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-secondary mb-2">Build Learning Library</h3>
                <p className="text-foreground/80">
                  Access your complete activity history, browse past activities, and track your child's holistic development journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Learning in Action Section */}
      <div className="bg-card py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-secondary mb-8">
            Learning in Action
          </h2>
          <p className="text-center text-foreground/80 text-lg mb-12 max-w-3xl mx-auto">
            See how Revivedu transforms everyday moments into rich learning experiences for Indian children
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-3xl overflow-hidden shadow-sm hover:shadow-pop transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1755534316951-8a2c8ad3b56f?crop=entropy&cs=srgb&fm=webp&w=600&q=75"
                srcSet="https://images.unsplash.com/photo-1755534316951-8a2c8ad3b56f?crop=entropy&cs=srgb&fm=webp&w=400&q=75 400w, https://images.unsplash.com/photo-1755534316951-8a2c8ad3b56f?crop=entropy&cs=srgb&fm=webp&w=800&q=75 800w"
                sizes="(max-width: 768px) 100vw, 33vw"
                alt="Indian child reading a book at home"
                loading="lazy"
                width="600"
                height="256"
                className="w-full h-64 object-cover"
              />
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-secondary mb-2">Reading & Exploration</h3>
                <p className="text-foreground/70">Building linguistic intelligence through stories and books</p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-sm hover:shadow-pop transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1624641328326-ecb54b7cff1f?crop=entropy&cs=srgb&fm=webp&w=600&q=75"
                srcSet="https://images.unsplash.com/photo-1624641328326-ecb54b7cff1f?crop=entropy&cs=srgb&fm=webp&w=400&q=75 400w, https://images.unsplash.com/photo-1624641328326-ecb54b7cff1f?crop=entropy&cs=srgb&fm=webp&w=800&q=75 800w"
                sizes="(max-width: 768px) 100vw, 33vw"
                alt="Indian child doing a hands-on craft activity"
                loading="lazy"
                width="600"
                height="256"
                className="w-full h-64 object-cover"
              />
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-secondary mb-2">Creative Expression</h3>
                <p className="text-foreground/70">Nurturing artistic and spatial intelligence through hands-on activities</p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-sm hover:shadow-pop transition-all duration-300">
              <img
                src="https://images.unsplash.com/photo-1769720205321-4ae3c8f8e4fc?crop=entropy&cs=srgb&fm=webp&w=600&q=75"
                srcSet="https://images.unsplash.com/photo-1769720205321-4ae3c8f8e4fc?crop=entropy&cs=srgb&fm=webp&w=400&q=75 400w, https://images.unsplash.com/photo-1769720205321-4ae3c8f8e4fc?crop=entropy&cs=srgb&fm=webp&w=800&q=75 800w"
                sizes="(max-width: 768px) 100vw, 33vw"
                alt="Indian child exploring a hands-on learning activity at home"
                loading="lazy"
                width="600"
                height="256"
                className="w-full h-64 object-cover"
              />
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-secondary mb-2">Hands-On Discovery</h3>
                <p className="text-foreground/70">Learning by doing - experiments and sensory play that spark curiosity</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="bg-muted py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-secondary mb-12">
            Platform Features
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-card rounded-3xl p-8 shadow-sm hover:shadow-pop transition-all duration-300" data-testid="feature-card-intelligences">
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-secondary mb-4">8 Multiple Intelligences Theory</h3>
              <p className="text-foreground/80">
                Activities designed around all 8 intelligence types for holistic development.
              </p>
            </div>
            <div className="bg-card rounded-3xl p-8 shadow-sm hover:shadow-pop transition-all duration-300" data-testid="feature-card-skills">
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Lightbulb className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-secondary mb-4">21st Century Skills & SEL</h3>
              <p className="text-foreground/80">
                Foster critical thinking, creativity, collaboration, communication, and social-emotional learning.
              </p>
            </div>
            <div className="bg-card rounded-3xl p-8 shadow-sm hover:shadow-pop transition-all duration-300" data-testid="feature-card-nios">
              <div className="bg-accent/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <BookOpen className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-secondary mb-4">NIOS Curriculum Access</h3>
              <p className="text-foreground/80">
                Structured access to NIOS curriculum for grades 10 and 12, supplemented with AI activities.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary mb-6">
            Start Your Personalized Learning Journey
          </h2>
          <p className="text-lg text-foreground/80 mb-8">
            Create your first personalized learning activity in minutes, tailored to your child's unique intelligence profile.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              data-testid="cta-generate-btn"
              size="lg"
              className="rounded-full px-10 py-6 text-lg font-bold shadow-pop hover:shadow-pop-hover transform hover:-translate-y-1 transition-all duration-300 bg-primary hover:bg-primary/90"
              onClick={() => navigate("/generate")}
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Generate Your First Activity
            </Button>
            <Button
              data-testid="guide-btn"
              size="lg"
              variant="outline"
              className="rounded-full px-10 py-6 text-lg font-bold border-2 border-secondary text-secondary hover:bg-secondary hover:text-white transition-all duration-300"
              onClick={() => navigate("/guide")}
            >
              <BookOpen className="mr-2 h-5 w-5" />
              Parent & Learner Guide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;